<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import FormAlert from "@/components/FormAlert.vue";
import TemplateAuthMethodForm from "./components/TemplateAuthMethodForm.vue";
import {
  useAuthoringTemplate,
  useDeleteAuthoringTemplate,
  usePublishAuthoringTemplate,
  useUpdateAuthoringTemplate,
} from "@/queries/connection-template-authoring";
import {
  RATE_LIMIT_FASTEST_SENDS_PER_SECOND,
  RATE_LIMIT_SLOWEST_PER_SECONDS,
  TEMPLATE_PROTOCOLS,
  type TemplateAuthMethodContent,
  type UpdateConnectionTemplateRequest,
} from "@/types";
import { ApiError } from "@/helpers/api-error";
import { useBbelFunctions } from "@/queries/expressions";
import { useConnectionTemplateScopes } from "./composables/useConnectionTemplateScope";

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id as string);

const {
  template,
  templateLoading: loading,
  templateError: error,
  refetchTemplate,
} = useAuthoringTemplate(id);
const {
  updateTemplate,
  updateTemplateAsync,
  updateTemplatePending,
  updateTemplateError,
  updateTemplateReset,
} = useUpdateAuthoringTemplate();
const {
  publishTemplate,
  publishTemplatePending,
  publishTemplateError,
  publishTemplateReset,
} = usePublishAuthoringTemplate();
const {
  deleteTemplate,
  deleteTemplatePending,
  deleteTemplateError,
  deleteTemplateReset,
} = useDeleteAuthoringTemplate();

// A stored value outside the supported set stays selectable — loading must never rewrite content.
const protocolOptions = computed(() => {
  const current = draft.value?.content.protocol;
  return current &&
    !TEMPLATE_PROTOCOLS.includes(current as (typeof TEMPLATE_PROTOCOLS)[number])
    ? [...TEMPLATE_PROTOCOLS, current]
    : [...TEMPLATE_PROTOCOLS];
});

// The designer edits a local draft of the updatable projection (name is create-time only). Server
// state only overwrites a clean draft, so a background refetch never clobbers in-progress edits.
const draft = ref<UpdateConnectionTemplateRequest | null>(null);

function serverSnapshot(): UpdateConnectionTemplateRequest | null {
  if (!template.value) return null;
  const { content, logoUrl, description } = template.value;
  return JSON.parse(
    JSON.stringify({
      content,
      logoUrl: logoUrl ?? null,
      description: description ?? null,
    })
  );
}

// A rate is two numbers or nothing, so each input writes into a whole rateLimit rather than a member of
// one. Clearing either leaves a half-filled rate on the draft; pruneDraft drops that at the boundary,
// which keeps the form usable while one number is being retyped.
function rateNumber(field: "sends" | "perSeconds") {
  return computed({
    get: () => draft.value?.content.rateLimit?.[field] ?? "",
    set: (typed: number | string) => {
      if (!draft.value) return;
      const current = draft.value.content.rateLimit ?? { sends: 0, perSeconds: 0 };
      const other = field === "sends" ? current.perSeconds : current.sends;
      const value = Number(typed);
      draft.value.content.rateLimit =
        !value && !other ? null : { ...current, [field]: value };
    },
  });
}

const rateSends = rateNumber("sends");
const ratePerSeconds = rateNumber("perSeconds");

const isDirty = computed(
  () =>
    !!draft.value &&
    JSON.stringify(draft.value) !== JSON.stringify(serverSnapshot())
);

watch(
  template,
  () => {
    if (!draft.value || !isDirty.value) draft.value = serverSnapshot();
  },
  { immediate: true }
);

function discard() {
  draft.value = serverSnapshot();
  updateTemplateReset();
}

// Unnamed field/variable rows are half-typed UI state, not content — dropped at the boundary.
// Likewise unfilled onResponse writes (the token/refresh panes seed the reserved token rows with
// empty values): an empty expression writes nothing, so it must not persist.
function pruneDraft(): UpdateConnectionTemplateRequest {
  const data: UpdateConnectionTemplateRequest = JSON.parse(
    JSON.stringify(draft.value)
  );
  const rate = data.content.rateLimit;
  if (rate && (!rate.sends || !rate.perSeconds)) data.content.rateLimit = null;
  for (const m of data.content.authMethods) {
    if (m.connection) m.connection = m.connection.filter((f) => f.name.trim());
    if (m.platform) m.platform = m.platform.filter((v) => v.name.trim());
    for (const handler of Object.values(m.http?.authFlow?.handlers ?? {})) {
      if (!handler.onResponse) continue;
      const kept = Object.entries(handler.onResponse).filter(([, expr]) =>
        expr.trim()
      );
      handler.onResponse = kept.length ? Object.fromEntries(kept) : null;
    }
    const t = m.http?.transportConfig;
    if (t) {
      if (t.mtls && !t.mtls.credentialRole.trim()) t.mtls = null;
      if (t.ntlm && !t.ntlm.username.trim() && !t.ntlm.password.trim())
        t.ntlm = null;
      if (!t.mtls && !t.ntlm && m.http) m.http.transportConfig = null;
    }
  }
  return data;
}

// Resync from the response, not the refetch — the server normalises the content shape
// (e.g. dropped nulls), and a shape-only difference must not read as still-dirty.
function resyncDraft(dto: {
  content: unknown;
  logoUrl?: string | null;
  description?: string | null;
}) {
  draft.value = JSON.parse(
    JSON.stringify({
      content: dto.content,
      logoUrl: dto.logoUrl ?? null,
      description: dto.description ?? null,
    })
  );
}

// Saving a published template applies live (the engine reads content live, ADR-019) and reverts it
// to Draft — so the Save button routes through a warning dialog carrying both commit intents.
const showSaveConfirm = ref(false);

function requestSave() {
  if (template.value?.status === "Published") showSaveConfirm.value = true;
  else save();
}

function save() {
  if (!draft.value) return;
  showSaveConfirm.value = false;
  updateTemplate(
    { id: id.value, data: pruneDraft() },
    { onSuccess: resyncDraft }
  );
}

async function saveAndRepublish() {
  if (!draft.value || !template.value) return;
  showSaveConfirm.value = false;
  try {
    const dto = await updateTemplateAsync({ id: id.value, data: pruneDraft() });
    resyncDraft(dto);
    publishTemplate(dto.id);
  } catch {
    // The reactive error refs drive the FormAlerts; nothing to add here.
  }
}

// ── Auth methods ───────────────────────────────────────────────────────────────────────────────

const activeMethod = ref(0);

const methods = computed(() => draft.value?.content.authMethods ?? []);

// Per-phase BBEL scopes for the active method's fields — mirror the server's
// ConnectionTemplateScopeSchema and track the draft live, so a field added above is immediately
// completable below.
const { functions: bbelFunctions } = useBbelFunctions();
const bbelScopes = useConnectionTemplateScopes(
  computed(() => methods.value[activeMethod.value]),
  bbelFunctions
);

function methodTitle(m: TemplateAuthMethodContent, index: number): string {
  return m.label || m.id || m.http?.authFlow?.kind || `Method ${index + 1}`;
}

// ── Publish-validation failures, anchored to their method (TemplateValidationProblemDetails) ────

// The method's selection id — mirrors the server's MethodId (explicit id, else the flow kind).
function methodId(m: TemplateAuthMethodContent): string {
  return m.id?.trim() || m.http?.authFlow?.kind || "";
}

const publishFailures = computed(() =>
  publishTemplateError.value instanceof ApiError
    ? publishTemplateError.value.templateFailures
    : []
);

const failuresByMethod = computed(() => {
  const groups = new Map<string, string[]>();
  for (const f of publishFailures.value)
    groups.set(f.methodId, [...(groups.get(f.methodId) ?? []), f.message]);
  return groups;
});

function methodHasFailures(m: TemplateAuthMethodContent): boolean {
  return failuresByMethod.value.has(methodId(m));
}

// Live duplicate-id check — mirrors the publish gate's DuplicateMethodId rule, warned as you type.
const duplicateMethodIds = computed(() => {
  const counts = new Map<string, number>();
  for (const m of methods.value) {
    const mid = methodId(m);
    counts.set(mid, (counts.get(mid) ?? 0) + 1);
  }
  return new Set([...counts].filter(([, n]) => n > 1).map(([mid]) => mid));
});

// Group heading: the method's display title, or the raw anchor id if the method is gone.
function methodHeading(mid: string): string {
  const i = methods.value.findIndex((m) => methodId(m) === mid);
  return i >= 0 ? methodTitle(methods.value[i], i) : mid;
}

// A rejected publish jumps to the first failing method so the badge and the form line up.
watch(publishFailures, (failures) => {
  if (!failures.length) return;
  const first = methods.value.findIndex(methodHasFailures);
  if (first >= 0) activeMethod.value = first;
});

function patchMethod(index: number, value: TemplateAuthMethodContent) {
  if (!draft.value) return;
  draft.value.content.authMethods = methods.value.map((m, i) =>
    i === index ? value : m
  );
}

function addMethod() {
  if (!draft.value) return;
  draft.value.content.authMethods = [
    ...methods.value,
    { http: { authFlow: { kind: "none" } }, connection: [] },
  ];
  activeMethod.value = methods.value.length - 1;
}

function removeMethod(index: number) {
  if (!draft.value) return;
  draft.value.content.authMethods = methods.value.filter((_, i) => i !== index);
  activeMethod.value = Math.max(
    0,
    Math.min(activeMethod.value, methods.value.length - 1)
  );
}

// Structure removals confirm (rows don't); the removal stays local until Save Changes.
const confirmRemoveMethod = ref<number | null>(null);

function confirmedRemoveMethod() {
  if (confirmRemoveMethod.value !== null)
    removeMethod(confirmRemoveMethod.value);
  confirmRemoveMethod.value = null;
}

// ── Delete ─────────────────────────────────────────────────────────────────────────────────────

const showDeleteConfirm = ref(false);

function confirmDelete() {
  if (!template.value) return;
  deleteTemplate(template.value.id, {
    onSuccess: () => router.push("/admin/connection-templates"),
    onError: () => (showDeleteConfirm.value = false),
  });
}
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <SkeletonLoader
        v-if="loading || error"
        type="header"
        :error="error"
        @retry="refetchTemplate"
      />

      <template v-else-if="template && draft">
        <PageHeader
          :title="template.name"
          :breadcrumbs="[
            { label: 'Dashboard', href: '/' },
            {
              label: 'Connection Templates',
              href: '/admin/connection-templates',
            },
            { label: template.name },
          ]"
        >
          <!-- Lifecycle actions live with the status badge; form actions sit at the bottom of the
               form per the Action Button Rules (DESIGN-GUIDELINES.md). -->
          <template #actions>
            <span
              class="badge text-uppercase align-self-center"
              :class="
                template.status === 'Published' ? 'bg-success' : 'bg-secondary'
              "
              >{{ template.status }}</span
            >
            <button
              class="bb-btn-secondary-cta"
              :disabled="
                template.status === 'Published' ||
                isDirty ||
                publishTemplatePending
              "
              :title="isDirty ? 'Save your changes first' : undefined"
              @click="publishTemplate(template.id)"
            >
              <span
                v-if="publishTemplatePending"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              Publish
            </button>
          </template>
        </PageHeader>

        <FormAlert
          :error="updateTemplateError"
          @dismiss="updateTemplateReset"
        />
        <!-- Structured publish rejection: failures grouped by method, badged on the tabs below.
             The generic alert stays the fallback for 400s without the typed failure list. -->
        <div
          v-if="publishFailures.length"
          class="alert alert-danger alert-dismissible"
        >
          <strong>This template can't publish yet.</strong>
          <div
            v-for="[mid, messages] in failuresByMethod"
            :key="mid"
            class="mt-2"
          >
            <div class="fw-semibold">{{ methodHeading(mid) }}</div>
            <ul class="mb-0">
              <li v-for="msg in messages" :key="msg">{{ msg }}</li>
            </ul>
          </div>
          <button
            type="button"
            class="btn-close"
            @click="publishTemplateReset"
          ></button>
        </div>
        <FormAlert
          v-else
          :error="publishTemplateError"
          @dismiss="publishTemplateReset"
        />
        <FormAlert
          :error="deleteTemplateError"
          @dismiss="deleteTemplateReset"
        />

        <div class="row g-3 mb-4">
          <div class="col-12 col-md-6">
            <label class="form-label" for="tpl-description">Description</label>
            <input
              id="tpl-description"
              v-model="draft.description"
              type="text"
              class="form-control"
              placeholder="Shown in the New Connection picker"
            />
          </div>
          <div class="col-12 col-md-6">
            <label class="form-label" for="tpl-logo">Logo URL</label>
            <input
              id="tpl-logo"
              v-model="draft.logoUrl"
              type="text"
              class="form-control"
            />
          </div>
          <div class="col-6 col-md-3">
            <label class="form-label" for="tpl-vendor">Vendor</label>
            <input
              id="tpl-vendor"
              v-model="draft.content.vendor"
              type="text"
              class="form-control"
            />
          </div>
          <div class="col-6 col-md-3">
            <label class="form-label" for="tpl-protocol">Protocol</label>
            <select
              id="tpl-protocol"
              v-model="draft.content.protocol"
              class="form-select"
            >
              <option v-for="p in protocolOptions" :key="p" :value="p">
                {{ p }}
              </option>
            </select>
          </div>
          <div class="col-6 col-md-3">
            <label class="form-label" for="tpl-rate-sends">Rate limit</label>
            <div class="input-group">
              <input
                id="tpl-rate-sends"
                v-model="rateSends"
                type="number"
                min="1"
                :max="RATE_LIMIT_FASTEST_SENDS_PER_SECOND * RATE_LIMIT_SLOWEST_PER_SECONDS"
                class="form-control"
                placeholder="sends"
              />
              <span class="input-group-text">per</span>
              <input
                id="tpl-rate-seconds"
                v-model="ratePerSeconds"
                type="number"
                min="1"
                :max="RATE_LIMIT_SLOWEST_PER_SECONDS"
                class="form-control"
                placeholder="secs"
              />
            </div>
            <div class="form-text">
              As the vendor documents it. Empty holds them to
              {{ RATE_LIMIT_FASTEST_SENDS_PER_SECOND }} a second.
            </div>
          </div>
        </div>

        <h3>Auth methods</h3>
        <ul class="nav nav-tabs">
          <li v-for="(m, i) in methods" :key="i" class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeMethod === i }"
              @click="activeMethod = i"
            >
              {{ methodTitle(m, i) }}
              <i
                v-if="methodHasFailures(m)"
                class="bi bi-exclamation-circle-fill text-danger ms-1"
                title="This method has publish-blocking failures"
              ></i>
              <!-- The remove lives on the object it removes — active tab only. -->
              <i
                v-if="activeMethod === i"
                class="bi bi-x ms-1"
                role="button"
                title="Remove this auth method"
                @click.stop="confirmRemoveMethod = i"
              ></i>
            </button>
          </li>
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              title="Add auth method"
              @click="addMethod"
            >
              <i class="bi bi-plus-lg"></i>
            </button>
          </li>
        </ul>
        <div class="border border-top-0 rounded-bottom p-3">
          <!-- Keyed by tab: row editors hold local entry state, so switching methods must remount. -->
          <TemplateAuthMethodForm
            v-if="methods[activeMethod]"
            :key="activeMethod"
            :model-value="methods[activeMethod]"
            :scopes="bbelScopes"
            :id-conflict="
              duplicateMethodIds.has(methodId(methods[activeMethod]))
            "
            @update:model-value="patchMethod(activeMethod, $event)"
            @remove="removeMethod(activeMethod)"
          />
          <p v-else class="text-muted mb-0">
            No auth methods yet — a template needs at least one way to connect
            before it can publish.
          </p>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <button
            v-if="template.status === 'Draft'"
            type="button"
            class="bb-btn-danger btn-sm"
            @click="showDeleteConfirm = true"
          >
            <i class="bi bi-trash"></i> Delete
          </button>
          <button
            type="button"
            class="bb-btn-cancel"
            :disabled="!isDirty || updateTemplatePending"
            @click="discard"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            :disabled="!isDirty || updateTemplatePending"
            @click="requestSave"
          >
            <span
              v-if="updateTemplatePending"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="bi bi-save me-2"></i>Save Changes
          </button>
        </div>
      </template>
    </PanelCard>
  </PanelGrid>

  <div
    class="modal fade"
    :class="{ show: showDeleteConfirm }"
    :style="{ display: showDeleteConfirm ? 'block' : 'none' }"
    @click.self="showDeleteConfirm = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">Delete template</h2>
          <button
            type="button"
            class="btn-close"
            @click="showDeleteConfirm = false"
          ></button>
        </div>
        <div class="modal-body">
          Delete the draft <strong>{{ template?.name }}</strong
          >? This cannot be undone.
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="showDeleteConfirm = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleteTemplatePending"
            @click="confirmDelete"
          >
            <span
              v-if="deleteTemplatePending"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showDeleteConfirm" class="modal-backdrop fade show"></div>

  <div
    class="modal fade"
    :class="{ show: confirmRemoveMethod !== null }"
    :style="{ display: confirmRemoveMethod !== null ? 'block' : 'none' }"
    @click.self="confirmRemoveMethod = null"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">Remove auth method</h2>
          <button
            type="button"
            class="btn-close"
            @click="confirmRemoveMethod = null"
          ></button>
        </div>
        <div class="modal-body">
          Remove
          <strong>{{
            confirmRemoveMethod !== null
              ? methodTitle(methods[confirmRemoveMethod], confirmRemoveMethod)
              : ""
          }}</strong>
          with its fields, variables, and auth flow? Removals stay local until
          you save changes.
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="confirmRemoveMethod = null"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-danger"
            @click="confirmedRemoveMethod"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="confirmRemoveMethod !== null"
    class="modal-backdrop fade show"
  ></div>

  <div
    class="modal fade"
    :class="{ show: showSaveConfirm }"
    :style="{ display: showSaveConfirm ? 'block' : 'none' }"
    @click.self="showSaveConfirm = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-warning-emphasis">
            <i class="bi bi-exclamation-triangle me-2"></i>This template is
            published
          </h2>
          <button
            type="button"
            class="btn-close"
            @click="showSaveConfirm = false"
          ></button>
        </div>
        <div class="modal-body">
          <p class="mb-2">
            Saved changes apply <strong>immediately</strong> to every connection
            using this template — the engine reads template content live.
          </p>
          <p class="mb-0">
            <strong>Save &amp; Republish</strong> keeps it available in the New
            Connection picker. <strong>Save as Draft</strong> also removes it
            from the picker until it is republished.
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="showSaveConfirm = false"
          >
            Cancel
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="save">
            Save as Draft
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="saveAndRepublish"
          >
            Save &amp; Republish
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showSaveConfirm" class="modal-backdrop fade show"></div>
</template>
